import { useState } from 'react';
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = 'https://api.theonestack.com/api/contact';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function LeadCaptureSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState({ name: '', email: '', company: '', size: '', message: '' });

  function onChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, product: 'ams' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      setStatus('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  }

  return (
    <section id="contact" className="relative py-24 sm:py-32 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #0a0f0d 0%, #0d1a15 50%, #0a0f0d 100%)' }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 55% 45% at 50% 50%, rgba(20,184,166,0.07) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-2xl px-6">
        <div className="text-center reveal mb-12">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Talk to Our Team
          </h2>
          <p className="mt-4 text-lg text-zinc-400">
            Have questions about TheOne AMS? We'd love to help you get started.
          </p>
        </div>

        {status === 'success' ? (
          <div className="rounded-2xl border border-teal-500/20 bg-teal-500/5 p-10 text-center reveal">
            <CheckCircle className="mx-auto mb-4 h-12 w-12 text-teal-400" />
            <h3 className="text-xl font-semibold text-white mb-2">Message received!</h3>
            <p className="text-zinc-400">We'll be in touch within one business day.</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5 reveal">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                  Your name <span className="text-teal-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  placeholder="Jane Smith"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                  Work email <span className="text-teal-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  placeholder="jane@association.org"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">Organization</label>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  placeholder="Acme Association"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">Member count</label>
                <select
                  name="size"
                  value={form.size}
                  onChange={onChange}
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
                >
                  <option value="">Select…</option>
                  <option>Under 500 members</option>
                  <option>500–2,000 members</option>
                  <option>2,000–10,000 members</option>
                  <option>10,000+ members</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                What are you hoping to achieve?
              </label>
              <textarea
                name="message"
                rows={4}
                value={form.message}
                onChange={onChange}
                placeholder="Tell us about your current workflow challenges or what you'd like to improve…"
                className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 transition resize-none"
              />
            </div>
            {status === 'error' && (
              <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {errorMsg}
              </div>
            )}
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="group flex w-full items-center justify-center gap-2 rounded-xl bg-teal-500 px-8 py-4 text-base font-semibold text-white transition hover:bg-teal-600 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? 'Sending…' : 'Send Message'}
              {status !== 'submitting' && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
            </button>
            <p className="text-center text-xs text-zinc-500">No spam. We respond within one business day.</p>
          </form>
        )}
      </div>
    </section>
  );
}
